
import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useToast } from "@/components/ui/use-toast";

interface EnhancedPDFGenerationOptions {
  filename?: string;
  scale?: number;
  paperFormat?: "a4" | "letter";
  orientation?: "portrait" | "landscape";
  margin?: number;
  quality?: number;
  timeout?: number;
  includeMetadata?: boolean;
  reportType?: string;
  userInfo?: {
    name?: string;
    email?: string;
  };
}

export const useEnhancedPDFGeneration = () => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  
  const generateEnhancedPDF = async (
    elementRef: React.RefObject<HTMLDivElement>,
    options: EnhancedPDFGenerationOptions = {}
  ) => {
    if (!elementRef.current) return null;
    
    try {
      setIsGenerating(true);
      
      toast({
        title: "Generating Enhanced PDF Report",
        description: "Please wait while we create your comprehensive report..."
      });

      const {
        filename = generateSmartFilename(options.reportType),
        scale = 2,
        paperFormat = "a4",
        orientation = "portrait",
        margin = 15,
        quality = 0.9,
        timeout = 30000,
        includeMetadata = true,
        reportType = "report",
        userInfo
      } = options;
      
      // Enhanced timeout handling
      const timeoutPromise = new Promise<null>((_, reject) => {
        timeoutRef.current = window.setTimeout(() => {
          reject(new Error("PDF generation timed out"));
        }, timeout);
      });
      
      // Enhanced canvas generation with better options
      const canvasPromise = html2canvas(elementRef.current, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 20000,
        allowTaint: false,
        foreignObjectRendering: true,
        onclone: (clonedDoc) => {
          // Enhanced styling for PDF generation
          const element = clonedDoc.querySelector('[data-pdf-content]') || clonedDoc.body;
          if (element) {
            // Ensure consistent styling
            element.setAttribute('style', `
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              line-height: 1.5;
              color: #1f2937;
              background: white;
              padding: 20px;
              width: 100%;
              box-sizing: border-box;
            `);
            
            // Fix any problematic elements
            const problematicElements = element.querySelectorAll('.chart-container, canvas:not([data-keep]), .animate-spin, .animate-pulse');
            problematicElements.forEach(el => {
              el.setAttribute('data-html2canvas-ignore', 'true');
            });

            // Ensure images are loaded
            const images = element.querySelectorAll('img');
            images.forEach(img => {
              if (!img.complete) {
                img.style.display = 'none';
              }
            });
          }
        }
      });
      
      const canvas = await Promise.race([canvasPromise, timeoutPromise]) as HTMLCanvasElement;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Enhanced PDF creation
      const imgData = canvas.toDataURL('image/jpeg', quality);
      
      // Calculate dimensions
      let pdfWidth, pdfHeight;
      if (paperFormat === 'a4') {
        pdfWidth = 210;
        pdfHeight = 297;
      } else {
        pdfWidth = 215.9;
        pdfHeight = 279.4;
      }
      
      if (orientation === 'landscape') {
        [pdfWidth, pdfHeight] = [pdfHeight, pdfWidth];
      }
      
      // Create PDF with enhanced options
      const pdf = new jsPDF(orientation, 'mm', paperFormat);
      
      // Add metadata if requested
      if (includeMetadata) {
        pdf.setProperties({
          title: `EquiForge™ ${reportType} Report`,
          subject: `Generated ${reportType} report`,
          author: userInfo?.name || 'EquiForge™ User',
          creator: 'EquiForge™ Platform'
        });
      }
      
      // Calculate image dimensions with proper scaling
      const contentWidth = pdfWidth - (margin * 2);
      const imgAspectRatio = canvas.height / canvas.width;
      const imgWidth = contentWidth;
      const imgHeight = contentWidth * imgAspectRatio;
      
      // Handle multi-page content
      const maxPageHeight = pdfHeight - (margin * 2);
      let yPosition = margin;
      let remainingHeight = imgHeight;
      let sourceY = 0;
      
      while (remainingHeight > 0) {
        const pageHeight = Math.min(remainingHeight, maxPageHeight);
        const sourceHeight = (pageHeight / imgHeight) * canvas.height;
        
        // Create a temporary canvas for this page section
        const pageCanvas = document.createElement('canvas');
        const pageCtx = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        
        if (pageCtx) {
          pageCtx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
          const pageImgData = pageCanvas.toDataURL('image/jpeg', quality);
          
          pdf.addImage(pageImgData, 'JPEG', margin, yPosition, imgWidth, pageHeight);
        }
        
        remainingHeight -= pageHeight;
        sourceY += sourceHeight;
        
        if (remainingHeight > 0) {
          pdf.addPage();
          yPosition = margin;
        }
      }
      
      // Add enhanced footer with timestamp and branding
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        
        // Footer text
        const footerText = `Generated by EquiForge™ • ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
        const pageText = `Page ${i} of ${pageCount}`;
        
        pdf.text(footerText, margin, pdfHeight - 5);
        pdf.text(pageText, pdfWidth - margin - pdf.getTextWidth(pageText), pdfHeight - 5);
      }
      
      // Save the PDF
      pdf.save(filename);
      
      toast({
        title: "Enhanced PDF Report Generated",
        description: `Your ${reportType} report has been downloaded as ${filename}`
      });
      
      return filename;
      
    } catch (error) {
      console.error("Enhanced PDF generation error:", error);
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      toast({
        title: "PDF Generation Failed",
        description: "We couldn't generate your PDF. Please try again or use the text report option.",
        variant: "destructive"
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSmartFilename = (reportType?: string) => {
    const timestamp = new Date().toLocaleDateString().replace(/\//g, '-');
    const timeString = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    }).replace(':', '');
    
    const type = reportType || 'report';
    return `equiforge-${type}-${timestamp}-${timeString}.pdf`;
  };

  return {
    generateEnhancedPDF,
    isGenerating,
    generateSmartFilename
  };
};
